import {
  Component,
  computed,
  effect,
  inject,
  input,
  InputSignal,
  Signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { SectionComponent } from '../../../../../components/layout/section/section.component';
import { SectionHeadlineComponent } from '../../../../../components/layout/section/section-header/section-headline/section-headline.component';
import { ButtonComponent } from '../../../../../components/feedback/button/button.component';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { CustomTileSectionStore } from '../../../../../services/api/custom-tile-section/custom-tile-section-store.service';
import { CustomTileSectionFormsFactoryService } from '../../../../../factory/customTileSection/custom-tile-section-forms-factory';
import { FormTracking } from '../../../form-tracking/form-tracking.component';
import {
  EEntityType,
  ERelationType,
  ICustomTile,
  ICustomTileSection,
  INormalText,
} from '@web/dtos';
import { ICustomTileForm } from '../../../../../entities/custom-tile.entity';
import { ToggleComponent } from '../../../../../components/data-input/form-inputs/toggle/toggle.component';
import { TextDirective } from 'ngx-color-picker';
import { DateInputComponent } from '../../../../../components/data-input/form-inputs/date-input/date-input/date-input.component';
import { ERoute } from '../../../../../constants/routes.enum';
import { TranslationTextInputComponent } from '../../../../../components/data-input/form-inputs/translation-text-input/translation-text-input.component';
import { ImageSelectComponent } from '../../../../../components/data-input/form-inputs/image-select/image-select.component';
import { TextInputComponent } from '../../../../../components/data-input/form-inputs/text-input/text-input.component';
import { CmsTextProviderService } from '../../../../../services/cms-text-provider.service';
import { LanguageSwitcherComponent } from '../../../../../components/data-input/form-inputs/language-switcher/language-switcher.component';
import { map, Observable } from 'rxjs';
import { IDialogData } from '../../../../../components/dialogs/dialog';
import { ResetChangesDialogComponent } from '../../../../../components/dialogs/reset-changes-dialog/reset-changes-dialog.component';
import { DialogService } from '../../../../../services/support/dialog/dialog.service';
import { CustomTileStore } from '../../../../../services/api/custom-tile/custom-tile-store.service';
import { TranslationAccessibilityTextInputComponent } from '../../../../../components/data-input/form-inputs/translation-accessibility-text-input/translation-accessibility-text-input.component';
import { TranslationTextEditorComponent } from '../../../../../components/data-input/form-inputs/translation-text-editor/translation-text-editor.component';
import { LoadingAnimationComponent } from '../../../../../components/feedback/loading-animation/loading-animation.component';

@Component({
  selector: 'aw-start-page-card-edit-page',
  templateUrl: './start-page-card-edit-page.component.html',
  imports: [
    SectionComponent,
    SectionHeadlineComponent,
    ButtonComponent,
    ReactiveFormsModule,
    ToggleComponent,
    TextDirective,
    DateInputComponent,
    TranslationTextInputComponent,
    ImageSelectComponent,
    TextInputComponent,
    LanguageSwitcherComponent,
    TranslationAccessibilityTextInputComponent,
    TranslationTextEditorComponent,
    LoadingAnimationComponent,
  ],
})
export class StartPageCardEditPageComponent extends FormTracking<ICustomTileForm> {
  public readonly cardId: InputSignal<string> = input.required();
  public readonly sectionId: InputSignal<string> = input.required();

  private readonly customTileSectionStore: CustomTileSectionStore = inject(
    CustomTileSectionStore
  );
  private readonly customTileStore: CustomTileStore = inject(CustomTileStore);
  private readonly customTileSectionFactory: CustomTileSectionFormsFactoryService =
    inject(CustomTileSectionFormsFactoryService);
  private readonly router: Router = inject(Router);
  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  private readonly dialogService: DialogService = inject(DialogService);
  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );

  protected readonly isLoading: Signal<boolean> =
    this.customTileSectionStore.isLoading;

  public tile!: ICustomTile;
  public tileForm: FormGroup<ICustomTileForm> | undefined;
  public language: FormControl<string> =
    this.formBuilder.nonNullable.control<string>('');

  protected readonly ERelationType: typeof ERelationType = ERelationType;
  protected readonly EEntityType: typeof EEntityType = EEntityType;

  public readonly section: Signal<ICustomTileSection> = computed(
    (): ICustomTileSection => {
      const section: ICustomTileSection | undefined =
        this.customTileSectionStore
          .customTileSections()
          .find(section => section.id === this.sectionId());
      if (!section) {
        this.router.navigate([
          `/${ERoute.cms}/${ERoute.general}/${ERoute.startPage}`,
        ]);
      }
      return section!;
    }
  );

  constructor() {
    super();
    effect(() => {
      const tileMatch: ICustomTile | undefined = this.section()?.tiles?.find(
        (tile: ICustomTile) => tile.id === this.cardId()
      );
      if (tileMatch) {
        this.tile = tileMatch;
        this.tileForm = this.customTileSectionFactory.buildCustomTileFormGroup(
          this.tile
        );
        this.initFormTracking(this.tileForm);
        this.tileForm.updateValueAndValidity();
      }
    });
  }

  ngOnInit(): void {
    window.onbeforeunload = (event: BeforeUnloadEvent) => {
      if (this.tileForm?.dirty) {
        event.preventDefault();
      }
    };
  }

  public saveTile(): void {
    if (this.tileForm?.valid) {
      const tileFormValue: FormGroup<ICustomTileForm>['value'] =
        this.tileForm.value;
      const tile: ICustomTile = {
        id: tileFormValue.id,
        isActive: tileFormValue.isActive,
        accessibleImage: tileFormValue.accessibleImage,
        texts: (tileFormValue.title as INormalText[])
          .concat(tileFormValue.description as INormalText[])
          .concat(tileFormValue.linkText as INormalText[]),
        link: tileFormValue.link,
        startDate: tileFormValue.startDate,
        endDate: tileFormValue.endDate,
      } as ICustomTile;
      if (tile.accessibleImage) {
        if (!tile.accessibleImage.image) {
          tile.accessibleImage.image = null;
        }
      }
      this.customTileStore.save(tile).then(result => {
        if (result.message?.type === 'success') {
          this.resetFormChanges();
        }
      });
    }
  }

  public resetFormChanges(): void {
    this.customTileSectionStore.reload();
  }

  public handleAbortChangesDialog(): Observable<boolean> {
    const dialogRef = this.dialogService.openDialog<IDialogData>(
      ResetChangesDialogComponent,
      {
        headline: this.cmsTextProvider.shared.revertChanges,
        width: '600px',
        icon: 'warning',
        iconBackgroundColor: '#eed202',
      }
    );
    dialogRef.closed.subscribe(result => {
      if (result?.submit) {
        this.customTileSectionStore.reload();
      }
    });
    return dialogRef.closed.pipe(map(result => result?.submit ?? false));
  }

  protected correctDateValues(event: boolean): void {
    if (!event) {
      this.tileForm?.controls.startDate.setValue(null);
      this.tileForm?.controls.endDate.setValue(null);
    }
  }
}
